import { createSlice, createAsyncThunk, createEntityAdapter, createSelector } from '@reduxjs/toolkit';
import tagsAPI from '@/api/tags';
import { normalizeData } from '@/utils/jsonapi';

/**
 * Entity adapter for tags
 */
const tagsAdapter = createEntityAdapter({
  selectId: (tag) => tag.id,
  sortComparer: (a, b) => (a.name || '').localeCompare(b.name || ''),
});

/**
 * Async thunk to fetch all tags for the current tenant
 */
export const fetchTagsThunk = createAsyncThunk(
  'tags/fetchAll',
  async (queryOptions = {}, { rejectWithValue }) => {
    try {
      const response = await tagsAPI.getAll(queryOptions);

      // Handle both DRF pagination and JSON:API formats
      const results = response.results || response.data || [];
      const normalized = normalizeData(results);

      return {
        data: normalized || [],
      };
    } catch (error) {
      return rejectWithValue(error.apiError?.message || error.message);
    }
  }
);

/**
 * Async thunk to create a new tag
 */
export const createTagThunk = createAsyncThunk(
  'tags/create',
  async (data, { rejectWithValue }) => {
    try {
      const jsonApiResponse = await tagsAPI.create(data);
      return normalizeData(jsonApiResponse.data);
    } catch (error) {
      return rejectWithValue(error.apiError?.message || error.message);
    }
  }
);

/**
 * Async thunk to update a tag
 */
export const updateTagThunk = createAsyncThunk(
  'tags/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const jsonApiResponse = await tagsAPI.update(id, data);
      return normalizeData(jsonApiResponse.data);
    } catch (error) {
      return rejectWithValue(error.apiError?.message || error.message);
    }
  }
);

/**
 * Async thunk to delete a tag
 */
export const deleteTagThunk = createAsyncThunk(
  'tags/delete',
  async (id, { rejectWithValue }) => {
    try {
      await tagsAPI.delete(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.apiError?.message || error.message);
    }
  }
);

/**
 * Tags slice with state management for tag CRUD operations
 */
const tagsSlice = createSlice({
  name: 'tags',
  initialState: tagsAdapter.getInitialState({
    loading: false,
    error: null,
  }),
  reducers: {
    clearTagsError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Tags
      .addCase(fetchTagsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTagsThunk.fulfilled, (state, action) => {
        state.loading = false;
        tagsAdapter.setAll(state, action.payload?.data || []);
      })
      .addCase(fetchTagsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create Tag
      .addCase(createTagThunk.pending, (state) => {
        state.error = null;
      })
      .addCase(createTagThunk.fulfilled, (state, action) => {
        tagsAdapter.addOne(state, action.payload);
      })
      .addCase(createTagThunk.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Update Tag
      .addCase(updateTagThunk.fulfilled, (state, action) => {
        tagsAdapter.upsertOne(state, action.payload);
      })
      .addCase(updateTagThunk.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Delete Tag
      .addCase(deleteTagThunk.fulfilled, (state, action) => {
        tagsAdapter.removeOne(state, action.payload);
      })
      .addCase(deleteTagThunk.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { clearTagsError } = tagsSlice.actions;

// Entity adapter selectors
const getTagsState = (state) => {
  return state.tags || tagsAdapter.getInitialState();
};

export const {
  selectAll: selectAllTags,
  selectById: selectTagById,
  selectIds: selectTagIds,
} = tagsAdapter.getSelectors(getTagsState);

/**
 * Selector to get tags by an array of IDs
 * @param {Array<string>} ids - Array of tag IDs
 * @returns {Array<Object>} Array of matching tag objects
 */
export const selectTagsByIds = createSelector(
  [selectAllTags, (_, ids) => ids],
  (allTags, ids) => {
    if (!ids || ids.length === 0) return [];
    const idSet = new Set(ids.map(String));
    return allTags.filter((tag) => idSet.has(String(tag.id)));
  }
);

export const selectTagsLoading = (state) => state.tags?.loading || false;
export const selectTagsError = (state) => state.tags?.error || null;

export default tagsSlice.reducer;
